import { mean } from './descriptive';

export function pearsonR(xs: readonly number[], ys: readonly number[]): number {
  if (xs.length !== ys.length) {
    throw new Error('pearsonR: xs and ys must be the same length');
  }
  if (xs.length < 2) return Number.NaN;
  const mx = mean(xs);
  const my = mean(ys);
  let num = 0;
  let dx2 = 0;
  let dy2 = 0;
  for (let i = 0; i < xs.length; i++) {
    const dx = xs[i]! - mx;
    const dy = ys[i]! - my;
    num += dx * dy;
    dx2 += dx * dx;
    dy2 += dy * dy;
  }
  const denom = Math.sqrt(dx2 * dy2);
  if (denom === 0) return Number.NaN;
  return num / denom;
}

export interface LinearRegression {
  slope: number;
  intercept: number;
  rSquared: number;
}

export function linearRegression(xs: readonly number[], ys: readonly number[]): LinearRegression {
  if (xs.length !== ys.length) {
    throw new Error('linearRegression: xs and ys must be the same length');
  }
  if (xs.length < 2) {
    return { slope: Number.NaN, intercept: Number.NaN, rSquared: Number.NaN };
  }
  const mx = mean(xs);
  const my = mean(ys);
  let num = 0;
  let dx2 = 0;
  for (let i = 0; i < xs.length; i++) {
    const dx = xs[i]! - mx;
    num += dx * (ys[i]! - my);
    dx2 += dx * dx;
  }
  if (dx2 === 0) {
    return { slope: Number.NaN, intercept: Number.NaN, rSquared: Number.NaN };
  }
  const slope = num / dx2;
  const intercept = my - slope * mx;
  const r = pearsonR(xs, ys);
  return { slope, intercept, rSquared: r * r };
}
